import { Badge } from '@medikei/ui';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import type { Commission } from '../types/partner';
import { Card, CardContent, CardHeader, CardTitle } from '@medikei/ui';

interface CommissionTableProps {
  commissions: Commission[] | Array<{
    id: string;
    amount: number;
    status: string;
    createdAt: string | Date;
    patientName?: string;
    serviceType?: string;
    conversionSource?: string;
  }>;
  title?: string;
  maxItems?: number;
}

const statusConfig: Record<string, { variant: 'warning' | 'success' | 'primary' | 'danger'; label: string }> = {
  PENDING: { variant: 'warning', label: 'Pendente' },
  APPROVED: { variant: 'success', label: 'Aprovado' },
  PAID: { variant: 'primary', label: 'Pago' },
  CANCELLED: { variant: 'danger', label: 'Cancelado' },
};

export function CommissionTable({
  commissions,
  title = 'Últimas Comissões',
  maxItems,
}: CommissionTableProps) {
  const displayedCommissions = maxItems ? commissions.slice(0, maxItems) : commissions;

  return (
    <Card className="rounded-2xl border border-border overflow-hidden">
      <CardHeader className="p-4 sm:p-6 pb-2 sm:pb-3">
        <CardTitle className="text-base sm:text-lg font-bold">{title}</CardTitle>
      </CardHeader>
      <CardContent className="p-0 sm:p-6 sm:pt-0">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border bg-muted/20 sm:bg-transparent">
                <th className="text-left py-3 px-3 sm:px-4 text-xs sm:text-sm font-semibold text-muted-foreground">
                  Data
                </th>
                <th className="text-right py-3 px-3 sm:px-4 text-xs sm:text-sm font-semibold text-muted-foreground">
                  Valor
                </th>
                <th className="text-right py-3 px-3 sm:px-4 text-xs sm:text-sm font-semibold text-muted-foreground">
                  Status
                </th>
              </tr>
            </thead>
            <tbody>
              {displayedCommissions.length === 0 ? (
                <tr>
                  <td colSpan={3} className="py-8 text-center text-xs sm:text-sm text-muted-foreground">
                    Nenhuma comissão encontrada
                  </td>
                </tr>
              ) : (
                displayedCommissions.map((commission) => (
                  <tr
                    key={commission.id}
                    className="border-b border-border/60 hover:bg-muted/40 transition-colors"
                  >
                    <td className="py-3 px-3 sm:px-4 text-xs sm:text-sm text-foreground whitespace-nowrap">
                      {format(new Date(commission.createdAt), "dd MMM yyyy", {
                        locale: ptBR,
                      })}
                    </td>
                    <td className="py-3 px-3 sm:px-4 text-xs sm:text-sm font-bold text-foreground text-right whitespace-nowrap">
                      R$ {commission.amount.toLocaleString('pt-BR', {
                        minimumFractionDigits: 2,
                      })}
                    </td>
                    <td className="py-3 px-3 sm:px-4 text-right whitespace-nowrap">
                      <Badge variant={statusConfig[commission.status]?.variant || 'warning'}>
                        {statusConfig[commission.status]?.label || commission.status}
                      </Badge>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}